import type { ThreadListItem } from "../../shared/types";

const VIEW_TITLES: Record<string, string> = {
  inbox: "Inbox",
  now: "Needs you now",
  vendor: "Waiting on vendor",
  fyi: "FYI",
  snoozed: "Snoozed",
  drafts: "Drafts",
  sent: "Sent",
  archive: "Archive",
  spam: "Spam",
  trash: "Trash",
  all: "All Mail",
};

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString();
}

export function ThreadList({
  view,
  query,
  onQueryChange,
  threads,
  loading,
  error,
  stillSyncing,
  importProgress,
  selectedId,
  onSelect,
  onRefresh,
}: {
  view: string;
  query: string;
  onQueryChange: (q: string) => void;
  threads: ThreadListItem[];
  loading: boolean;
  error: string | null;
  stillSyncing: boolean;
  importProgress: { indexedCount: number; totalEstimate: number | null } | null;
  selectedId: string | null;
  onSelect: (t: ThreadListItem) => void;
  onRefresh: () => void;
}) {
  return (
    <div className="gm-list">
      <div className="gm-list-header">
        <input className="input" placeholder="Search mail  /" value={query} onChange={(e) => onQueryChange(e.target.value)} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <div style={{ fontSize: 15, fontWeight: 500 }}>{VIEW_TITLES[view] ?? view}</div>
          <div style={{ fontSize: 11, color: "var(--gm-text-4)" }}>{loading ? "…" : `${threads.length} threads`}</div>
          <button
            className="btn btn-ghost"
            style={{ marginLeft: "auto", width: 32, height: 32, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
            onClick={onRefresh}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <span style={{ display: "inline-block", fontSize: 20, lineHeight: 1, transition: "transform 0.4s ease", transform: loading ? "rotate(360deg)" : "none" }}>
              ⟳
            </span>
          </button>
        </div>
      </div>
      <div className="gm-list-scroll">
        {error && (
          <div className="card" style={{ margin: "0 0 8px", color: "var(--lb-red-2)", fontSize: 12 }}>
            Couldn't load mail: {error}
          </div>
        )}
        {stillSyncing && (
          <div className="card" style={{ margin: "0 0 8px", color: "var(--gm-text-3)", fontSize: 12 }}>
            {importProgress
              ? `Importing your mail — ${importProgress.indexedCount.toLocaleString()}${
                  importProgress.totalEstimate ? ` of ~${importProgress.totalEstimate.toLocaleString()}` : ""
                } imported so far…`
              : "Importing your mail — more threads will appear shortly…"}
          </div>
        )}
        {threads.map((t, i) => (
          <div key={t.id} className="gm-row" data-selected={t.id === selectedId} onClick={() => onSelect(t)}>
            <div className="avatar gm-row-avatar" style={{ opacity: i % 2 ? 0.85 : 1 }}>
              {(t.company || t.from || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="gm-row-body">
              <div className="gm-row-top">
                {t.unread && <span className="gm-unread-dot" />}
                <span className="gm-row-company">{t.company || t.from}</span>
                {t.hasAttachments && (
                  <span title="Has attachments" style={{ fontSize: 11, opacity: 0.7, flexShrink: 0 }}>
                    📎
                  </span>
                )}
                <span className="gm-row-time">{relativeTime(t.timeIso)}</span>
              </div>
              <div className="gm-row-subject" style={{ fontWeight: t.assignee ? 400 : 500 }}>{t.subject || "(no subject)"}</div>
              <div className="gm-row-preview">{t.preview}</div>
              <div className="gm-row-meta">
                {!t.labelIds.length && t.category && <span className="chip chip-neutral">{t.category}</span>}
                {t.messageCount > 1 && <span style={{ fontSize: 11, color: "var(--gm-text-4)" }}>{t.messageCount} messages</span>}
                {t.hasDraft && <span className="chip chip-neutral">Draft</span>}
                {t.noteCount > 0 && <span className="chip chip-neutral gm-pulse">{t.noteCount} note{t.noteCount > 1 ? "s" : ""}</span>}
              </div>
            </div>
          </div>
        ))}
        {!loading && threads.length === 0 && (
          <div style={{ padding: 22, textAlign: "center", color: "var(--gm-text-4)", fontSize: 12.5 }}>Nothing here.</div>
        )}
      </div>
    </div>
  );
}
